/**
 * Server backup operations using ShareVault API
 */

import type { EnsureServerBackupResult, KeyshareBackupData, VaultEncryptionMethod } from '../types';
import type { TokenRefreshApiClient } from '../token-refresh-client';
import {
  base64ToBytes,
  bytesToBase64,
  envelopeDecryptKeyshareWithDek,
  envelopeDecryptKeyshareWithKek,
  envelopeDecryptKeyshareWithPassword,
  envelopeEncryptKeyshare,
  envelopeEncryptKeyshareBasic,
  envelopeEncryptKeyshareWithPasskeyKek,
  envelopeEncryptKeyshareWithPassword,
  type EnvelopeEncryptedShare,
  type ServerManagedKdf,
  type UserProtection
} from './crypto-utils';

declare const __EMBARK_SHARE_VAULT_URL__: string

const SHARE_VAULT_URL =
  (typeof __EMBARK_SHARE_VAULT_URL__ !== 'undefined' && __EMBARK_SHARE_VAULT_URL__) ||
  (typeof (window as any).__EMBARK_SERVICES__ !== 'undefined' && (window as any).__EMBARK_SERVICES__?.shareVaultUrl) ||
  'https://api.embarkai.io/vault';

/**
 * Vault URL of the backup of one wallet (sdk #32): a user has one wallet per namespace, the vault one backup
 * per wallet. Without `owner` the vault answers about the user's single share, as for older SDKs.
 */
function shareUrl(path: string, owner?: string): string {
  return `${SHARE_VAULT_URL}/v1/shares/me${path}${owner ? `?owner=${encodeURIComponent(owner.toLowerCase())}` : ''}`;
}

function sameOwner(a: string | null | undefined, b: string | null | undefined): boolean {
  return !!a && !!b && a.toLowerCase() === b.toLowerCase();
}

/** The share names the wallet it holds, so the vault keeps it apart from the user's other wallets */
function withOwner<T extends object>(envelope: T, data: Pick<KeyshareBackupData, 'ownerAddress'>): T & { ownerAddress?: string } {
  return data.ownerAddress ? { ...envelope, ownerAddress: data.ownerAddress } : envelope;
}

/**
 * Get ShareVault token from TSS (via TokenRefreshApiClient for 401/refresh handling)
 */
async function getShareVaultToken(
  tokenClient: TokenRefreshApiClient,
  scopes: string[],
  accessToken?: string,
  projectId?: string
): Promise<string> {
  const data = await tokenClient.apiCall(
    'POST',
    '/api/auth/token/exchange',
    {
      scopes,
      audience: 'lumia-passport-share-vault-service', // TODO: embarkai audience ?
      ttl: 300,
    },
    projectId,
    accessToken
  );

  if (!data?.resourceToken) {
    throw new Error('Invalid token response');
  }

  return data.resourceToken;
}

/**
 * Upload encrypted share to ShareVault
 */
async function uploadShareToVault(
  tokenClient: TokenRefreshApiClient,
  encryptedShare: any,
  accessToken?: string,
  projectId?: string,
  ifMatch?: string
): Promise<void> {
  const token = await getShareVaultToken(tokenClient, ['share:put'], accessToken, projectId);
  const idempotencyKey = crypto.randomUUID ? crypto.randomUUID() : `backup-${Date.now()}`;

  const response = await fetch(shareUrl('', encryptedShare.ownerAddress), {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Idempotency-Key': idempotencyKey,
      ...(ifMatch ? { 'If-Match': ifMatch } : {}),
    },
    body: JSON.stringify(encryptedShare),
  });

  if (response.status === 412 && ifMatch) throw new VaultError(412, 'The backup changed meanwhile, please try again');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to upload share: ${response.status} ${response.statusText} - ${errorText}`);
  }
}

/**
 * Backup keyshare to server using ShareVault API
 */
export async function backupToServer(
  data: KeyshareBackupData,
  tokenClient: TokenRefreshApiClient,
  password?: string,
  accessToken?: string,
  projectId?: string,
  /** etag of the share this backup replaces: 412 when it changed meanwhile */
  ifMatch?: string
): Promise<void> {
  let encryptedShare: any;

  if (password) {
    encryptedShare = await envelopeEncryptKeyshareWithPassword(data, password);
  } else {
    encryptedShare = await envelopeEncryptKeyshare(data, data.userId);
  }

  await uploadShareToVault(tokenClient, withOwner(encryptedShare, data), accessToken, projectId, ifMatch);
}

/**
 * Passkey backup with a KEK the caller already derived (so the user is asked for the passkey once)
 */
export async function backupToServerWithPasskeyKek(
  data: KeyshareBackupData,
  kek: Uint8Array | ArrayBuffer,
  credentialId: string,
  tokenClient: TokenRefreshApiClient,
  accessToken?: string,
  projectId?: string,
  /** etag of the share this backup replaces: 412 when it changed meanwhile */
  ifMatch?: string
): Promise<void> {
  const envelope = await envelopeEncryptKeyshareWithPasskeyKek(data, kek, credentialId);
  await uploadShareToVault(tokenClient, withOwner(envelope, data), accessToken, projectId, ifMatch);
}

/**
 * Download encrypted share from ShareVault
 */
async function downloadShareFromVault(
  tokenClient: TokenRefreshApiClient,
  accessToken?: string,
  projectId?: string,
  /** the vault only knows `rewrap`; the upgrade read keeps that label */
  purpose?: 'rewrap',
  /** the wallet whose backup is read */
  owner?: string
): Promise<any> {
  const token = await getShareVaultToken(tokenClient, ['share:get'], accessToken, projectId);

  const response = await fetch(shareUrl('', owner), {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'X-Client-Device-Id': 'lumia-ui-kit',
      'X-Client-Device-Name': 'Lumia UI Kit',
      // a read for an upgrade is still counted by the vault, only labelled so it does not look like a restore
      ...(purpose ? { 'X-Share-Read-Purpose': purpose } : {}),
    },
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('No backup found on server for this user');
    }
    const errorText = await response.text();
    throw new Error(`Failed to download share: ${response.status} ${response.statusText} - ${errorText}`);
  }

  const envelope = await response.json();
  if (!envelope.ciphertext_share || !envelope.wrapped_dek || !envelope.kdf || !envelope.alg || !envelope.aad) {
    throw new Error('Invalid envelope structure received from Share Vault');
  }

  return envelope;
}

/**
 * Restore keyshare from server using ShareVault API
 */
export async function restoreFromServer(
  userId: string,
  tokenClient: TokenRefreshApiClient,
  password?: string,
  accessToken?: string,
  projectId?: string,
  /** true only on an iframe session (off unless passed): otherwise a Basic backup is refused, as the vault does */
  basicAllowed = false,
  /** owner address of the wallet to restore (the server's keyshare for this project); older parents do not send it */
  expectedOwner?: string
): Promise<KeyshareBackupData> {
  console.log('[iframe][restoreFromServer] Starting restore for userId:', userId);
  console.log('[iframe][restoreFromServer] Using password:', !!password);

  // Download encrypted share from vault
  console.log('[iframe][restoreFromServer] Downloading share from vault...');
  let envelope: any;
  try {
    envelope = await downloadShareFromVault(tokenClient, accessToken, projectId, undefined, expectedOwner);
  } catch (error) {
    if (String((error as Error)?.message).includes(IFRAME_SESSION_REQUIRED)) throw basicNeedsIframeSession();
    throw error;
  }
  if (envelope.encryptionMethod === 'server-managed' && !basicAllowed && !password) throw basicNeedsIframeSession();

  console.log('[iframe][restoreFromServer] Envelope encryption method:', envelope.encryptionMethod);

  // Import decrypt functions from crypto-utils
  const { envelopeDecryptKeyshare, envelopeDecryptKeyshareWithPassword } = await import('./crypto-utils');

  // Decrypt based on encryption method or user preference.
  // Basic (server-managed) backups restore silently: TSS unwraps the DEK.
  let backupData: KeyshareBackupData;
  if (password) {
    backupData = await envelopeDecryptKeyshareWithPassword(envelope, password);
  } else if (envelope.encryptionMethod === 'server-managed') {
    const opened = await openBasicEnvelope(tokenClient, envelope, accessToken, projectId);
    backupData = opened.data;
    if (opened.rewrappedDek && backupData.userId === userId) {
      await storeRewrappedDek(tokenClient, envelope, opened.rewrappedDek, accessToken, projectId, expectedOwner);
    }
  } else {
    backupData = await envelopeDecryptKeyshare(envelope, userId);
  }

  if (backupData.userId !== userId) {
    throw new Error('Server backup does not match current user');
  }

  // A backup written before backups named their wallet may hold another wallet of this user
  if (!envelope.ownerAddress) await bindShareToWallet(tokenClient, envelope, backupData.ownerAddress, accessToken, projectId, expectedOwner);
  if (expectedOwner && !sameOwner(backupData.ownerAddress, expectedOwner)) throw new WalletMismatchError(backupData);

  console.log('[iframe][restoreFromServer] Decryption successful, returning backup data');
  return backupData;
}

/** The vault backup holds another wallet of this user than the one asked for: this wallet has no backup */
export class WalletMismatchError extends Error {
  constructor(readonly backup: KeyshareBackupData) {
    super(`${WALLET_MISMATCH}: the server backup belongs to another wallet (${backup.ownerAddress})`);
    this.name = 'WalletMismatchError';
  }
}

/** Prefix of the error of a restore whose backup belongs to another wallet of the user */
export const WALLET_MISMATCH = 'WALLET_MISMATCH';

/**
 * Record in a share of unknown owner (written before backups named their wallet) the wallet found in it,
 * so the vault keeps it as that wallet's backup and gives the other wallets their own. Best effort.
 * @returns false when the vault did not take it
 */
async function bindShareToWallet(
  tokenClient: TokenRefreshApiClient,
  envelope: { wrapped_dek: string; etag?: string },
  ownerAddress: string,
  accessToken?: string,
  projectId?: string,
  /** the wallet the share was read for */
  readAs?: string
): Promise<boolean> {
  // a rewrap goes only against the etag of the share that was read
  if (!ownerAddress || !envelope.etag) return false;
  try {
    await rewrapShareInVault(tokenClient, { wrapped_dek: envelope.wrapped_dek, ownerAddress }, envelope.etag, accessToken, projectId, readAs ?? ownerAddress);
    console.log('[iframe][server-backup] Backup bound to its wallet:', ownerAddress);
    return true;
  } catch (error) {
    console.warn('[iframe][server-backup] Backup not bound to its wallet:', errorMessage(error));
    return false;
  }
}

// ---------------------------------------------------------------------------
// Basic (server-managed) backups
// ---------------------------------------------------------------------------

/**
 * Prefix of every error the parent must treat as "TSS could not wrap or unwrap the Basic DEK". The name predates
 * the DEK wrap; it stays because published parents read it.
 */
export const RECOVERY_KEK_UNAVAILABLE = 'RECOVERY_KEK_UNAVAILABLE';

/** Vault and TSS refusal of Basic material to a session that is not an iframe session */
export const IFRAME_SESSION_REQUIRED = 'iframe_session_required';

/**
 * What an older SDK (no iframe sessions) shows when the vault backup is Basic: it can never open it, and it shows
 * the error text as it is, so the text says what the user can do instead of a code.
 */
export const BASIC_BACKUP_NEEDS_NEWER_APP =
  'This wallet is backed up by embarkAI and can be restored only in apps that use the latest embarkAI wallet. ' +
  'Sign in to such an app, or restore from a backup file.';

/** The restore error text for the parent: an older SDK gets `BASIC_BACKUP_NEEDS_NEWER_APP` for a Basic backup */
export function restoreErrorForParent(message: string, parentSupportsIframeSession: boolean): string {
  return !parentSupportsIframeSession && message.includes(IFRAME_SESSION_REQUIRED) ? BASIC_BACKUP_NEEDS_NEWER_APP : message;
}

function basicNeedsIframeSession(): BasicRecoveryError {
  return new BasicRecoveryError(true, `${IFRAME_SESSION_REQUIRED}: a Basic backup opens only after signing in again`);
}

export class BasicRecoveryError extends Error {
  constructor(
    readonly disabled: boolean,
    detail: string,
    /** HTTP status of the TSS answer; 400 = TSS refused this envelope (it will never open), absent = no answer */
    readonly status?: number
  ) {
    super(`${RECOVERY_KEK_UNAVAILABLE}: ${detail}`);
    this.name = 'BasicRecoveryError';
  }
}

export class VaultError extends Error {
  constructor(
    readonly status: number,
    message: string
  ) {
    super(message);
    this.name = 'VaultError';
  }
}

/** A Basic DEK wrapped by TSS: `wrapped_dek` and the kdf to keep in the envelope */
export interface WrappedRecoveryDek {
  wrappedDek: string;
  kdf: ServerManagedKdf;
}

export interface UnwrappedRecoveryDek {
  dek: Uint8Array;
  /** The DEK wrapped with the latest root key version: the envelope in the vault should be updated */
  rewrappedDek?: string;
}

/** A TSS recovery call; refusals become BasicRecoveryError */
async function recoveryCall(
  tokenClient: TokenRefreshApiClient,
  path: 'wrap' | 'unwrap',
  body: Record<string, string>,
  accessToken?: string,
  projectId?: string
): Promise<any> {
  try {
    return await tokenClient.apiCall('POST', `/api/auth/backup/recovery-dek/${path}`, body, projectId, accessToken);
  } catch (error: any) {
    // Permanent refusals mean Basic is not available here, so the parent falls back to the forced backup:
    // the flag is off, TSS has no such route yet (plain 404 page), or this iframe origin is not allowed
    const status = error?.details?.status ?? error?.status;
    const disabled = error?.code === 'RECOVERY_DISABLED' || error?.code === 'ORIGIN_NOT_ALLOWED' || status === 404;
    throw new BasicRecoveryError(disabled, error?.message || 'request failed', typeof status === 'number' ? status : undefined);
  }
}

/** Ask TSS to wrap the DEK of a new Basic backup with its recovery root */
export async function wrapRecoveryDek(
  tokenClient: TokenRefreshApiClient,
  dek: Uint8Array,
  accessToken?: string,
  projectId?: string
): Promise<WrappedRecoveryDek> {
  const data = await recoveryCall(tokenClient, 'wrap', { dek: bytesToBase64(dek) }, accessToken, projectId);
  if (typeof data?.wrapped_dek !== 'string' || !data.wrapped_dek || data?.kdf?.prf !== 'server-managed') {
    throw new BasicRecoveryError(false, 'invalid response');
  }
  return { wrappedDek: data.wrapped_dek, kdf: { prf: 'server-managed', hkdf: data.kdf.hkdf, info: data.kdf.info } };
}

/** Ask TSS to unwrap the DEK of an existing Basic backup. The DEK stays in the iframe. */
export async function unwrapRecoveryDek(
  tokenClient: TokenRefreshApiClient,
  wrappedDek: string,
  accessToken?: string,
  projectId?: string
): Promise<UnwrappedRecoveryDek> {
  const data = await recoveryCall(tokenClient, 'unwrap', { wrapped_dek: wrappedDek }, accessToken, projectId);
  const dek = typeof data?.dek === 'string' ? base64ToBytes(data.dek) : undefined;
  if (!dek || dek.length !== 32) throw new BasicRecoveryError(false, 'invalid response');
  const rewrapped = data.rewrapped_dek;
  return { dek, ...(typeof rewrapped === 'string' && rewrapped && rewrapped !== wrappedDek ? { rewrappedDek: rewrapped } : {}) };
}

/** Vault rewrap of the share that was read (`If-Match`); only the fields given change */
async function rewrapShareInVault(
  tokenClient: TokenRefreshApiClient,
  body: { wrapped_dek: string; kdf?: Partial<ServerManagedKdf>; ownerAddress?: string },
  /** required: without it the vault compares against its own read, not against the share this rewrap was computed from */
  etag: string,
  accessToken?: string,
  projectId?: string,
  /** the wallet the share was read for */
  owner?: string
): Promise<string | undefined> {
  if (!etag) throw new VaultError(428, 'A rewrap needs the etag of the share it was computed from');
  const token = await getShareVaultToken(tokenClient, ['share:rewrap'], accessToken, projectId);
  const response = await fetch(shareUrl('/rewrap', owner), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'Idempotency-Key': idempotencyKey('rewrap'),
      'If-Match': etag
    },
    body: JSON.stringify(body)
  });
  if (response.status === 412) throw new VaultError(412, 'The backup changed meanwhile');
  if (!response.ok) throw new VaultError(response.status, `Failed to rewrap share: ${response.status} ${await response.text()}`);
  // the etag of the share after the rewrap, for a next rewrap of the same read
  return ((await response.json().catch(() => null)) as { etag?: string } | null)?.etag;
}

/**
 * After a TSS root key rotation the unwrap also returns the DEK rewrapped with the latest key version (TSS checked
 * that it opens to the same DEK): store it as the envelope `wrapped_dek`; the share ciphertext stays the same.
 * Best effort: the old `wrapped_dek` keeps working, so a failure (a newer share meanwhile, vault down) only means
 * the update is tried again on the next restore. On success `envelope` is updated to the share now in the vault,
 * so a later rewrap of the same read (the wallet bind) neither fails on the etag nor puts the old wrap back.
 */
async function storeRewrappedDek(
  tokenClient: TokenRefreshApiClient,
  envelope: { wrapped_dek: string; etag?: string },
  rewrappedDek: string,
  accessToken?: string,
  projectId?: string,
  owner?: string
): Promise<void> {
  if (!envelope.etag) return;
  try {
    const etag = await rewrapShareInVault(tokenClient, { wrapped_dek: rewrappedDek }, envelope.etag, accessToken, projectId, owner);
    envelope.wrapped_dek = rewrappedDek;
    envelope.etag = etag;
    console.log('[iframe][server-backup] Basic backup: wrapped DEK updated after a key rotation');
  } catch (error) {
    console.warn('[iframe][server-backup] Basic backup: wrapped DEK not updated:', errorMessage(error));
  }
}

/** Open a Basic envelope: TSS unwraps the DEK, the share is decrypted here */
async function openBasicEnvelope(
  tokenClient: TokenRefreshApiClient,
  envelope: EnvelopeEncryptedShare,
  accessToken?: string,
  projectId?: string
): Promise<{ data: KeyshareBackupData; rewrappedDek?: string }> {
  const { dek, rewrappedDek } = await unwrapRecoveryDek(tokenClient, envelope.wrapped_dek, accessToken, projectId);
  try {
    return { data: await envelopeDecryptKeyshareWithDek(envelope, dek), ...(rewrappedDek ? { rewrappedDek } : {}) };
  } finally {
    dek.fill(0);
  }
}

interface VaultRecoveryStats {
  created?: { encryptionMethod?: string };
  /** absent on vaults that predate security levels */
  securityLevel?: 'basic' | 'advanced' | null;
  /** wallet the share holds; null = written before shares named their wallet; absent on older vaults */
  ownerAddress?: string | null;
}

/** Stats of the backup of wallet `owner` (404 → null: that wallet has none) */
async function getVaultRecoveryStats(
  tokenClient: TokenRefreshApiClient,
  accessToken?: string,
  projectId?: string,
  owner?: string
): Promise<VaultRecoveryStats | null> {
  const token = await getShareVaultToken(tokenClient, ['share:get'], accessToken, projectId);
  const response = await fetch(shareUrl('/recovery/stats', owner), {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` }
  });
  if (response.status === 404) return null;
  if (!response.ok) throw new VaultError(response.status, `Failed to read recovery stats: ${response.status}`);
  return (await response.json()) as VaultRecoveryStats;
}

export function encryptionMethodOfStats(stats: VaultRecoveryStats | null): VaultEncryptionMethod | null {
  if (!stats) return null;
  const method = stats.created?.encryptionMethod;
  const userHeld = method === 'password' || method === 'passkey' ? method : 'passkey';

  // New vaults compute the level from the live share: it wins over the creation stats, which may lag
  if (stats.securityLevel === null) return null;
  if (stats.securityLevel === 'basic') return 'server-managed';
  if (stats.securityLevel === 'advanced') return userHeld;

  // Older vaults only keep the creation metadata; records without a method are passkey backups
  if (!stats.created) return null;
  return method === 'server-managed' ? 'server-managed' : userHeld;
}

/** The encryption method of the backup of wallet `owner` in the vault, or null when there is none */
export async function getServerBackupMethod(
  tokenClient: TokenRefreshApiClient,
  accessToken?: string,
  projectId?: string,
  owner?: string
): Promise<VaultEncryptionMethod | null> {
  return encryptionMethodOfStats(await getVaultRecoveryStats(tokenClient, accessToken, projectId, owner));
}

/**
 * Whose keyshare a Basic share of unknown owner holds, opened through TSS; the share is bound to that
 * wallet on the way. `other-unbound`: another wallet's, but the vault did not take the bind (a write in between,
 * vault unavailable), so it is still reported for this wallet. `unknown` when it cannot be opened (TSS or the
 * vault unavailable).
 */
async function walletOfUnboundBasicShare(
  data: KeyshareBackupData,
  tokenClient: TokenRefreshApiClient,
  accessToken?: string,
  projectId?: string
): Promise<'this' | 'other' | 'other-unbound' | 'unknown'> {
  try {
    const envelope = (await downloadShareFromVault(tokenClient, accessToken, projectId, 'rewrap', data.ownerAddress)) as EnvelopeEncryptedShare & {
      etag?: string;
      ownerAddress?: string;
    };
    if (envelope.encryptionMethod !== 'server-managed') return 'unknown';
    if (envelope.ownerAddress) return sameOwner(envelope.ownerAddress, data.ownerAddress) ? 'this' : 'other';
    const { data: stored } = await openBasicEnvelope(tokenClient, envelope, accessToken, projectId);
    if (stored.userId !== data.userId || !stored.ownerAddress) return 'unknown';
    const bound = await bindShareToWallet(tokenClient, envelope, stored.ownerAddress, accessToken, projectId, data.ownerAddress);
    if (sameOwner(stored.ownerAddress, data.ownerAddress)) return 'this';
    // another wallet's backup: until the vault knows it, a new backup of this wallet would be refused
    return bound ? 'other' : 'other-unbound';
  } catch (error) {
    console.warn('[iframe][server-backup] Basic backup of unknown wallet not checked:', errorMessage(error));
    return 'unknown';
  }
}

/**
 * Make sure the vault holds a backup, creating a Basic one when there is none.
 * Never overwrites an existing share: the upload is create-only (`If-None-Match: *`).
 */
export async function ensureServerManagedBackup(
  data: KeyshareBackupData | null,
  tokenClient: TokenRefreshApiClient,
  accessToken?: string,
  projectId?: string,
  /**
   * allowCreate false: report an existing backup, never create a Basic one (no iframe session, D12).
   * owner: the wallet to check when there is no local keyshare (`data` names it otherwise).
   */
  options: { allowCreate?: boolean; owner?: string } = {}
): Promise<EnsureServerBackupResult> {
  // The backup of this wallet: another wallet of the user (another namespace) may have one of its own
  const owner = data?.ownerAddress ?? options.owner;
  // Twice at most: a share found to be another wallet's whose bind did not go through is checked once more
  for (let attempt = 0; ; attempt++) {
    let stats: VaultRecoveryStats | null;
    try {
      stats = await getVaultRecoveryStats(tokenClient, accessToken, projectId, owner);
    } catch (error) {
      return { status: 'failed', reason: 'vault_unavailable', error: errorMessage(error) };
    }
    const existing = encryptionMethodOfStats(stats);
    if (!existing) break;
    // A share written before shares named their wallet may hold another wallet. A Basic one can be opened
    // here to tell; a password or passkey one cannot, and stays reported as the backup, as before.
    const unbound = stats?.ownerAddress === null;
    if (!unbound || existing !== 'server-managed' || options.allowCreate === false || !data) {
      return { status: 'exists', encryptionMethod: existing };
    }
    const wallet = await walletOfUnboundBasicShare(data, tokenClient, accessToken, projectId);
    if (wallet === 'other') {
      console.warn('[iframe][server-backup] The vault backup belongs to another wallet of this user: creating one for', data.ownerAddress);
      break;
    }
    if (wallet === 'other-unbound') {
      if (attempt === 0) continue;
      // this wallet has no backup, whatever the vault still reports for it
      return { status: 'failed', reason: 'vault_unavailable', otherWallet: true, error: 'The vault backup belongs to another wallet and could not be bound to it' };
    }
    return { status: 'exists', encryptionMethod: existing };
  }

  if (options.allowCreate === false) {
    return { status: 'failed', reason: 'disabled', error: 'Basic backups need an iframe session' };
  }
  if (!data) return { status: 'failed', reason: 'no_keyshare' };

  let built: { envelope: EnvelopeEncryptedShare; dek: Uint8Array };
  try {
    built = await envelopeEncryptKeyshareBasic(data, (dek) => wrapRecoveryDek(tokenClient, dek, accessToken, projectId));
  } catch (error) {
    const disabled = error instanceof BasicRecoveryError && error.disabled;
    return { status: 'failed', reason: disabled ? 'disabled' : 'kek_unavailable', error: errorMessage(error) };
  }
  const { envelope, dek } = built;

  try {
    // Self-check before anything is written: the envelope must open with the same key
    const roundTrip = await envelopeDecryptKeyshareWithDek(envelope, dek);
    if (roundTrip.keyshare !== data.keyshare || roundTrip.userId !== data.userId) {
      return { status: 'failed', reason: 'unknown', error: 'Backup self-check failed' };
    }
    // The wrapped DEK is the backup's only key: TSS must unwrap it once to the same DEK before the backup is written
    let unwrapped: UnwrappedRecoveryDek;
    try {
      unwrapped = await unwrapRecoveryDek(tokenClient, envelope.wrapped_dek, accessToken, projectId);
    } catch (error) {
      // 400: TSS refused it, it will never open; anything else is "cannot tell now"
      if (error instanceof BasicRecoveryError && error.status === 400) return { status: 'failed', reason: 'unknown', error: 'The new wrapped DEK does not open' };
      return { status: 'failed', reason: 'kek_unavailable', error: errorMessage(error) };
    }
    const opens = unwrapped.dek.length === dek.length && unwrapped.dek.every((byte, i) => byte === dek[i]);
    unwrapped.dek.fill(0);
    if (!opens) return { status: 'failed', reason: 'unknown', error: 'The new wrapped DEK does not open' };

    // Create-only, twice at most. A 412 means a backup was created meanwhile (another tab, the post-DKG backup)
    // and stays; or that the vault put this wallet's write in a slot another wallet took meanwhile, or refused it
    // over a share of unknown owner: then this wallet has no backup, and the vault chooses the slot again.
    for (let attempt = 0; attempt < 2; attempt++) {
      if (await uploadShareToVaultCreateOnly(tokenClient, withOwner(envelope, data), accessToken, projectId)) {
        return { status: 'created', encryptionMethod: 'server-managed' };
      }
      const method = await getServerBackupMethod(tokenClient, accessToken, projectId, data.ownerAddress).catch(() => null);
      if (method) return { status: 'exists', encryptionMethod: method };
    }
    return { status: 'failed', reason: 'vault_unavailable', error: 'The vault refused the backup of this wallet' };
  } catch (error) {
    if (error instanceof VaultError && error.status === 400) {
      // a vault that predates Basic backups rejects the method: keep the legacy forced backup flow
      return { status: 'failed', reason: 'disabled', error: errorMessage(error) };
    }
    return { status: 'failed', reason: error instanceof VaultError ? 'vault_unavailable' : 'unknown', error: errorMessage(error) };
  } finally {
    dek.fill(0);
  }
}

/** @returns false when a share already exists (412) */
async function uploadShareToVaultCreateOnly(
  tokenClient: TokenRefreshApiClient,
  envelope: EnvelopeEncryptedShare & { ownerAddress?: string },
  accessToken?: string,
  projectId?: string
): Promise<boolean> {
  const token = await getShareVaultToken(tokenClient, ['share:put'], accessToken, projectId);
  const response = await fetch(shareUrl('', envelope.ownerAddress), {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'Idempotency-Key': idempotencyKey('basic-backup'),
      'If-None-Match': '*'
    },
    body: JSON.stringify(envelope)
  });
  if (response.status === 412) return false;
  if (!response.ok) throw new VaultError(response.status, `Failed to upload share: ${response.status} ${await response.text()}`);
  return true;
}

export type UpgradeOutcome =
  | { kind: 'upgraded'; encryptionMethod: 'password' | 'passkey' }
  /**
   * the vault share is not a Basic backup of this keyshare: the caller falls back to a full backup,
   * replacing only the share that was read (`etag`)
   */
  | { kind: 'not-applicable'; reason: string; etag?: string };

/**
 * Basic → Advanced: encrypt the keyshare again under a new DEK wrapped with a user-held secret and
 * replace the Basic share with it (`If-Match`). The Basic DEK is not reused, so copies of the record
 * made while it was Basic (vault snapshots, replicas) cannot open the protected backup.
 * Until the vault accepts the replace the Basic share stays in place, so any failure leaves the
 * account recoverable.
 */
export async function upgradeServerManagedBackup(
  data: KeyshareBackupData,
  protection: UserProtection,
  tokenClient: TokenRefreshApiClient,
  accessToken?: string,
  projectId?: string
): Promise<UpgradeOutcome> {
  const envelope = (await downloadShareFromVault(tokenClient, accessToken, projectId, 'rewrap', data.ownerAddress)) as EnvelopeEncryptedShare & {
    etag?: string;
    ownerAddress?: string;
  };
  if (envelope.encryptionMethod !== 'server-managed') return { kind: 'not-applicable', reason: 'not a basic backup', etag: envelope.etag };

  // A temporary failure (Vault outage, rate limit, network, session) is "try again": the Basic share stays
  // and is not replaced unchecked. Only when the Basic key is refused for good (Basic off, TSS refusing this
  // envelope) can the share never be checked; the user can still protect the wallet: the caller uploads a new backup
  let stored: KeyshareBackupData;
  try {
    // the Basic share is replaced below, so a rewrapped DEK needs no update here
    ;({ data: stored } = await openBasicEnvelope(tokenClient, envelope, accessToken, projectId));
  } catch (error) {
    if (error instanceof BasicRecoveryError && !error.disabled && error.status !== 400) throw error;
    return { kind: 'not-applicable', reason: `basic backup cannot be opened: ${(error as Error)?.message ?? error}`, etag: envelope.etag };
  }

  // Another wallet of this user (a share written before shares named their wallet): it stays as that wallet's
  // backup, and this wallet gets a backup of its own (no etag: nothing is replaced)
  if (stored.userId === data.userId && stored.ownerAddress && !sameOwner(stored.ownerAddress, data.ownerAddress)) {
    if (!envelope.ownerAddress) await bindShareToWallet(tokenClient, envelope, stored.ownerAddress, accessToken, projectId, data.ownerAddress);
    return { kind: 'not-applicable', reason: 'vault backup belongs to another wallet' };
  }

  // The vault share must be the keyshare this device holds, otherwise the upgrade would replace a newer backup
  if (stored.userId !== data.userId || stored.keyshare !== data.keyshare) {
    return { kind: 'not-applicable', reason: 'vault backup does not match the local keyshare', etag: envelope.etag };
  }

  const protectedEnvelope =
    protection.method === 'password'
      ? await envelopeEncryptKeyshareWithPassword(data, protection.password)
      : await envelopeEncryptKeyshareWithPasskeyKek(data, protection.kek, protection.credentialId);

  // The new envelope must open before it replaces the Basic one
  const roundTrip =
    protection.method === 'password'
      ? await envelopeDecryptKeyshareWithPassword(protectedEnvelope, protection.password)
      : await envelopeDecryptKeyshareWithKek(protectedEnvelope, protection.kek);
  if (roundTrip.keyshare !== data.keyshare || roundTrip.userId !== data.userId) throw new Error('Backup self-check failed');

  await replaceShareInVault(tokenClient, withOwner(protectedEnvelope, data), envelope.etag, accessToken, projectId);
  return { kind: 'upgraded', encryptionMethod: protection.method };
}

/** Replace the share only if it is still the one that was read (412 otherwise) */
async function replaceShareInVault(
  tokenClient: TokenRefreshApiClient,
  envelope: EnvelopeEncryptedShare & { ownerAddress?: string },
  etag: string | undefined,
  accessToken?: string,
  projectId?: string
): Promise<void> {
  const token = await getShareVaultToken(tokenClient, ['share:put'], accessToken, projectId);
  const response = await fetch(shareUrl('', envelope.ownerAddress), {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'Idempotency-Key': idempotencyKey('upgrade'),
      ...(etag ? { 'If-Match': etag } : {})
    },
    body: JSON.stringify(envelope)
  });
  if (response.status === 412) throw new VaultError(412, 'The backup changed meanwhile, please try again');
  if (!response.ok) throw new VaultError(response.status, `Failed to upload share: ${response.status} ${await response.text()}`);
}

function idempotencyKey(prefix: string): string {
  return crypto.randomUUID ? crypto.randomUUID() : `${prefix}-${Date.now()}-${Math.random()}`;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
